/**
 * Stepin gateway가 발급하는 access token은 JWT다.
 * payload를 디코드하면 sub(=userId), email 등 클레임에 접근할 수 있다.
 *
 * 서명 검증은 하지 않는다 — gateway가 매 요청마다 검증하므로,
 * 클라에서는 표시용/UI 분기용으로만 사용해야 한다.
 */

export interface JwtPayload {
	sub?: string;
	id?: string;
	userId?: string;
	email?: string;
	stepinId?: string;
	authority?: string;
	iat?: number;
	exp?: number;
	[key: string]: unknown;
}

function base64UrlDecode(input: string): string {
	const padded = input.replace(/-/g, '+').replace(/_/g, '/');
	const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
	if (typeof atob === 'function') {
		try {
			const decoded = atob(padded + padding);
			// atob는 binary string을 반환하므로 UTF-8로 재해석.
			return decodeURIComponent(
				Array.from(decoded, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
			);
		} catch {
			return '';
		}
	}
	return '';
}

export function decodeJwt(token: string | null | undefined): JwtPayload | null {
	if (!token) return null;
	const parts = token.split('.');
	if (parts.length !== 3) return null;
	const payload = base64UrlDecode(parts[1]);
	if (!payload) return null;
	try {
		return JSON.parse(payload) as JwtPayload;
	} catch {
		return null;
	}
}

export function getUserIdFromToken(token: string | null | undefined): string | null {
	const payload = decodeJwt(token);
	if (!payload) return null;
	// stepin 게이트웨이 토큰은 userId 를 `data` 클레임(문자열)에 담는다. sub/id/userId 도 호환.
	const id =
		payload.sub ??
		payload.id ??
		payload.userId ??
		(typeof payload.data === 'string' ? payload.data : undefined);
	return typeof id === 'string' ? id : null;
}

export function getEmailFromToken(token: string | null | undefined): string | null {
	const payload = decodeJwt(token);
	if (!payload) return null;
	return typeof payload.email === 'string' ? payload.email : null;
}

/**
 * 토큰이 만료(또는 디코드 불가)인지. 표시용 휴리스틱 — 최종 판정은 gateway가 함.
 * exp 클레임(초 단위) 기준, 5초 leeway. exp가 없으면 만료로 보지 않음(보수적).
 */
export function isTokenExpired(token: string | null | undefined, leewaySeconds = 5): boolean {
	if (!token) return true;
	const payload = decodeJwt(token);
	if (!payload) return true; // 디코드 불가 = 무효
	if (typeof payload.exp !== 'number') return false;
	return payload.exp * 1000 <= Date.now() - leewaySeconds * 1000;
}
